import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { processingApi, organizationApi } from '../services/api';
import {
  ProcessingActivityCreate,
  ProcessingActivityUpdate,
  ProcessingActivity,
  LegalBasis,
  DataCategory,
  DataSubjectCategory,
  RiskLevel,
  ProcessingActivityTemplate,
  ProcessingTemplateCategory
} from '@dsgvo-buddy/types';

interface ProcessingWizardPageProps {
  editMode?: boolean;
}
import {
  Title,
  Text,
  Button,
  Input,
  TextArea,
  Select,
  Option,
  CheckBox,
  ProgressIndicator,
  FlexBox,
  Card,
  Label,
  Icon,
  MessageStrip,
  BusyIndicator,
  RadioButton
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import HelpTooltip from '../components/HelpTooltip';
import { fieldHelp } from '../helpers/helpTexts';

const legalBasisLabels: Record<LegalBasis, { label: string; description: string }> = {
  [LegalBasis.CONSENT]: { label: 'Einwilligung (Art. 6 Abs. 1 lit. a)', description: 'Die betroffene Person hat ihre Einwilligung gegeben.' },
  [LegalBasis.CONTRACT]: { label: 'Vertragserfüllung (Art. 6 Abs. 1 lit. b)', description: 'Erfüllung eines Vertrags erforderlich.' },
  [LegalBasis.LEGAL_OBLIGATION]: { label: 'Rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c)', description: 'Erfüllung einer rechtlichen Verpflichtung.' },
  [LegalBasis.VITAL_INTERESTS]: { label: 'Lebenswichtige Interessen (Art. 6 Abs. 1 lit. d)', description: 'Schutz lebenswichtiger Interessen.' },
  [LegalBasis.PUBLIC_INTEREST]: { label: 'Öffentliches Interesse (Art. 6 Abs. 1 lit. e)', description: 'Aufgabe im öffentlichen Interesse.' },
  [LegalBasis.LEGITIMATE_INTEREST]: { label: 'Berechtigtes Interesse (Art. 6 Abs. 1 lit. f)', description: 'Wahrung berechtigter Interessen.' }
};

const dataCategoryLabels: Record<DataCategory, string> = {
  [DataCategory.CONTACT]: 'Kontaktdaten (Name, Adresse, E-Mail, Telefon)',
  [DataCategory.IDENTIFICATION]: 'Identifikationsdaten (Ausweisnummer, Geburtsdatum)',
  [DataCategory.FINANCIAL]: 'Finanzdaten (Bankverbindung, Gehalt)',
  [DataCategory.EMPLOYMENT]: 'Beschäftigungsdaten (Position, Abteilung)',
  [DataCategory.HEALTH]: 'Gesundheitsdaten (besondere Kategorie - Art. 9 DSGVO)',
  [DataCategory.BEHAVIORAL]: 'Verhaltensdaten (Nutzungsverhalten, Präferenzen)',
  [DataCategory.LOCATION]: 'Standortdaten (GPS, IP-Adresse)',
  [DataCategory.COMMUNICATION]: 'Kommunikationsdaten (E-Mails, Chat-Verläufe)',
  [DataCategory.OTHER]: 'Sonstige Daten'
};

const dataSubjectLabels: Record<DataSubjectCategory, string> = {
  [DataSubjectCategory.EMPLOYEES]: 'Mitarbeiter',
  [DataSubjectCategory.CUSTOMERS]: 'Kunden',
  [DataSubjectCategory.PROSPECTS]: 'Interessenten',
  [DataSubjectCategory.SUPPLIERS]: 'Lieferanten',
  [DataSubjectCategory.APPLICANTS]: 'Bewerber',
  [DataSubjectCategory.WEBSITE_VISITORS]: 'Website-Besucher',
  [DataSubjectCategory.BUSINESS_PARTNERS]: 'Geschäftspartner',
  [DataSubjectCategory.OTHER]: 'Sonstige'
};

const riskLevelConfig: Record<RiskLevel, { label: string; description: string; icon: string; state: string }> = {
  [RiskLevel.LOW]: { label: 'Niedrig', description: 'Geringe Auswirkungen bei Datenschutzverletzung', icon: 'status-positive', state: 'Success' },
  [RiskLevel.MEDIUM]: { label: 'Mittel', description: 'Moderate Auswirkungen möglich', icon: 'status-critical', state: 'Warning' },
  [RiskLevel.HIGH]: { label: 'Hoch', description: 'Erhebliche Auswirkungen oder besondere Kategorien', icon: 'status-negative', state: 'Error' }
};

const steps = [
  { title: 'Grunddaten', icon: 'hint' },
  { title: 'Rechtsgrundlage', icon: 'legal-section' },
  { title: 'Daten', icon: 'database' },
  { title: 'Empfänger', icon: 'shipping-status' },
  { title: 'Löschung & Risiko', icon: 'shield' }
];

interface FormData extends Omit<ProcessingActivityCreate, 'organization_id'> {
  organization_id: number | null;
}

export default function ProcessingWizardPage({ editMode = false }: ProcessingWizardPageProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [templates, setTemplates] = useState<ProcessingActivityTemplate[]>([]);
  const [templateCategory, setTemplateCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(editMode);
  const [error, setError] = useState<string | null>(null);
  const [existingActivity, setExistingActivity] = useState<ProcessingActivity | null>(null);

  const [formData, setFormData] = useState<FormData>({
    organization_id: null,
    name: '',
    department: '',
    description: '',
    purpose: '',
    legal_basis: LegalBasis.CONTRACT,
    legal_basis_details: '',
    data_subjects: [],
    data_categories: [],
    recipients: [],
    retention_period: 36,
    retention_details: '',
    third_country_transfer: false,
    third_country_details: '',
    tom_reference: '',
    risk_level: RiskLevel.LOW
  });

  const [recipientInput, setRecipientInput] = useState('');

  useEffect(() => { loadData(); }, [id, editMode]);

  const loadData = async () => {
    const [orgsResponse, templatesResponse] = await Promise.all([
      organizationApi.getAll(),
      processingApi.getTemplates()
    ]);

    if (orgsResponse.success && orgsResponse.data) {
      const org = orgsResponse.data[0];
      if (org) {
        setFormData(prev => ({ ...prev, organization_id: org.id }));
        if (editMode && id) {
          const activityResponse = await processingApi.getById(parseInt(id), org.id);
          if (activityResponse.success && activityResponse.data) {
            const activity = activityResponse.data;
            setExistingActivity(activity);
            setFormData({
              organization_id: activity.organization_id,
              name: activity.name,
              department: activity.department,
              description: activity.description || '',
              purpose: activity.purpose,
              legal_basis: activity.legal_basis,
              legal_basis_details: activity.legal_basis_details || '',
              data_subjects: activity.data_subjects,
              data_categories: activity.data_categories,
              recipients: activity.recipients,
              retention_period: activity.retention_period,
              retention_details: activity.retention_details || '',
              third_country_transfer: activity.third_country_transfer,
              third_country_details: activity.third_country_details || '',
              tom_reference: activity.tom_reference || '',
              risk_level: activity.risk_level || RiskLevel.LOW
            });
          } else {
            navigate('/processing');
            return;
          }
        }
      }
    }

    if (templatesResponse.success && templatesResponse.data) setTemplates(templatesResponse.data);
    setInitialLoading(false);
  };

  const handleApplyTemplate = (template: ProcessingActivityTemplate) => {
    setFormData(prev => ({
      ...prev,
      name: template.name,
      department: template.department,
      description: template.description,
      purpose: template.purpose,
      legal_basis: template.legal_basis,
      legal_basis_details: template.legal_basis_details,
      data_subjects: [...template.data_subjects],
      data_categories: [...template.data_categories],
      recipients: [...template.recipients],
      retention_period: template.retention_period,
      retention_details: template.retention_details,
      third_country_transfer: template.third_country_transfer,
      risk_level: template.risk_level
    }));
  };

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayValue = <T,>(field: keyof FormData, value: T) => {
    const currentArray = formData[field] as T[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    updateFormData(field, newArray);
  };

  const addRecipient = () => {
    if (recipientInput.trim()) {
      updateFormData('recipients', [...formData.recipients, recipientInput.trim()]);
      setRecipientInput('');
    }
  };

  const removeRecipient = (index: number) => {
    updateFormData('recipients', formData.recipients.filter((_, i) => i !== index));
  };

  const validateStep = (): boolean => {
    switch (currentStep) {
      case 0: return !!(formData.name && formData.department);
      case 1: return !!(formData.purpose && formData.legal_basis);
      case 2: return formData.data_subjects.length > 0 && formData.data_categories.length > 0;
      case 3: return true;
      case 4: return formData.retention_period > 0;
      default: return false;
    }
  };

  const handleNext = () => {
    if (validateStep()) { setCurrentStep(prev => Math.min(prev + 1, 4)); setError(null); }
    else { setError('Bitte füllen Sie alle erforderlichen Felder aus.'); }
  };

  const handleBack = () => { setCurrentStep(prev => Math.max(prev - 1, 0)); setError(null); };

  const handleSubmit = async () => {
    if (!formData.organization_id) { setError('Organisation erforderlich'); return; }
    if (!validateStep()) { setError('Bitte füllen Sie alle erforderlichen Felder aus.'); return; }

    setLoading(true);
    setError(null);

    if (editMode && id && existingActivity) {
      const { organization_id: _orgId, ...restFormData } = formData;
      const updateData: ProcessingActivityUpdate = { ...restFormData };
      const response = await processingApi.update(parseInt(id), formData.organization_id, updateData);
      if (response.success && response.data) navigate(`/processing/${response.data.id}`);
      else setError(response.error || 'Fehler beim Aktualisieren');
    } else {
      const data: ProcessingActivityCreate = { ...formData, organization_id: formData.organization_id };
      const response = await processingApi.create(data);
      if (response.success && response.data) navigate(`/processing/${response.data.id}`);
      else setError(response.error || 'Fehler beim Erstellen');
    }
    setLoading(false);
  };

  // Step 1: Grunddaten
  const renderStep1 = () => (
    <div>
      {!editMode && templates.length > 0 && (() => {
        const categoryLabels: Record<string, string> = {
          [ProcessingTemplateCategory.HR]: 'Personal & HR',
          [ProcessingTemplateCategory.SALES_CRM]: 'Vertrieb & CRM',
          [ProcessingTemplateCategory.MARKETING]: 'Marketing & Kommunikation',
          [ProcessingTemplateCategory.FINANCE]: 'Finanzen & Buchhaltung',
          [ProcessingTemplateCategory.IT_SECURITY]: 'IT & Sicherheit',
          [ProcessingTemplateCategory.WEBSITE_DIGITAL]: 'Website & Digital',
          [ProcessingTemplateCategory.FACILITY]: 'Gebäude & Infrastruktur',
          [ProcessingTemplateCategory.COMMUNICATION]: 'Interne Kommunikation',
          [ProcessingTemplateCategory.LEGAL_COMPLIANCE]: 'Recht & Compliance',
          [ProcessingTemplateCategory.GENERAL]: 'Allgemein'
        };
        const categories = Object.values(ProcessingTemplateCategory).filter(cat =>
          templates.some(t => t.category === cat)
        );
        const filteredTemplates = templateCategory
          ? templates.filter(t => t.category === templateCategory)
          : [];

        return (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            background: 'var(--sapInformativeBackground, #e8f0fe)',
            borderRadius: '0.5rem',
            border: '1px solid var(--sapInformativeColor, #0070f2)',
            borderLeftWidth: '3px'
          }}>
            <Text style={{ fontWeight: '700', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem', color: 'var(--sapInformativeColor, #0070f2)' }}>
              Vorlage verwenden (optional)
            </Text>
            <Text style={{ fontSize: '0.8125rem', display: 'block', marginBottom: '0.75rem', color: 'var(--sapTextColor)' }}>
              Wählen Sie eine Kategorie und dann eine passende Vorlage. Alle Felder werden automatisch ausgefüllt und können anschließend angepasst werden.
            </Text>
            <FlexBox wrap="Wrap" style={{ gap: '0.75rem' }}>
              <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>Kategorie</Label>
                <Select
                  onChange={(e: any) => setTemplateCategory(e.detail.selectedOption.value)}
                  style={{ width: '100%' }}
                >
                  <Option value="">-- Kategorie wählen --</Option>
                  {categories.map(cat => (
                    <Option key={cat} value={cat}>
                      {categoryLabels[cat] || cat} ({templates.filter(t => t.category === cat).length})
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: '2 1 300px', minWidth: '250px' }}>
                <Label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>Vorlage</Label>
                <Select
                  onChange={(e: any) => {
                    const name = e.detail.selectedOption.value;
                    const template = templates.find(t => t.name === name);
                    if (template) handleApplyTemplate(template);
                  }}
                  style={{ width: '100%' }}
                  disabled={!templateCategory}
                >
                  <Option value="">{templateCategory ? `-- ${filteredTemplates.length} Vorlagen verfügbar --` : '-- Erst Kategorie wählen --'}</Option>
                  {filteredTemplates.map(t => (
                    <Option key={t.name} value={t.name}>{t.name}</Option>
                  ))}
                </Select>
              </div>
            </FlexBox>
            {templateCategory && filteredTemplates.length > 0 && (
              <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.5rem' }}>
                {filteredTemplates.length} Vorlagen in „{categoryLabels[templateCategory]}"
              </Text>
            )}
          </div>
        );
      })()}

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <Label required>Name der Verarbeitung</Label>
          <HelpTooltip text={fieldHelp.processing.name} inline />
        </div>
        <Input
          value={formData.name}
          onInput={(e: any) => updateFormData('name', e.target.value)}
          placeholder="z.B. Personalverwaltung, Kundenverwaltung"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <Label required>Abteilung</Label>
          <HelpTooltip text={fieldHelp.processing.department} inline />
        </div>
        <Input
          value={formData.department}
          onInput={(e: any) => updateFormData('department', e.target.value)}
          placeholder="z.B. Personal, Vertrieb, IT"
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Beschreibung</Label>
        <TextArea
          value={formData.description}
          onInput={(e: any) => updateFormData('description', e.target.value)}
          placeholder="Optionale Beschreibung der Verarbeitung"
          rows={4}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  // Step 2: Rechtsgrundlage
  const renderStep2 = () => (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <Label required>Zweck der Verarbeitung</Label>
          <HelpTooltip text={fieldHelp.processing.purpose} inline />
        </div>
        <TextArea
          value={formData.purpose}
          onInput={(e: any) => updateFormData('purpose', e.target.value)}
          placeholder="Beschreiben Sie den konkreten Zweck der Datenverarbeitung"
          rows={4}
          style={{ width: '100%' }}
        />
        <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.25rem' }}>
          Der Zweck muss spezifisch, eindeutig und legitim sein.
        </Text>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <Label required>Rechtsgrundlage</Label>
          <HelpTooltip text={fieldHelp.processing.legal_basis} inline />
        </div>
        <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
          {Object.entries(legalBasisLabels).map(([key, { label, description }]) => {
            const isSelected = formData.legal_basis === key;
            return (
              <div
                key={key}
                onClick={() => updateFormData('legal_basis', key as LegalBasis)}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: isSelected
                    ? '2px solid var(--sapBrandColor)'
                    : '1px solid var(--sapField_BorderColor)',
                  backgroundColor: isSelected ? 'rgba(0, 112, 242, 0.06)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <RadioButton
                  name="legal_basis"
                  text={label}
                  checked={isSelected}
                  onChange={() => updateFormData('legal_basis', key as LegalBasis)}
                />
                <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginLeft: '1.75rem', display: 'block' }}>
                  {description}
                </Text>
              </div>
            );
          })}
        </FlexBox>
      </div>

      <div>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Zusätzliche Details zur Rechtsgrundlage</Label>
        <TextArea
          value={formData.legal_basis_details}
          onInput={(e: any) => updateFormData('legal_basis_details', e.target.value)}
          placeholder="Optionale Erläuterungen"
          rows={3}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  // Step 3: Daten
  const renderStep3 = () => (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <Label required>Betroffene Personen</Label>
          <HelpTooltip text={fieldHelp.processing.data_subjects} inline />
        </div>
        <FlexBox direction="Column" style={{ gap: '0.375rem' }}>
          {Object.entries(dataSubjectLabels).map(([key, label]) => (
            <CheckBox
              key={key}
              checked={formData.data_subjects.includes(key as DataSubjectCategory)}
              onChange={() => toggleArrayValue('data_subjects', key as DataSubjectCategory)}
              text={label}
            />
          ))}
        </FlexBox>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <Label required>Datenkategorien</Label>
          <HelpTooltip text={fieldHelp.processing.data_categories} inline />
        </div>
        <FlexBox direction="Column" style={{ gap: '0.375rem' }}>
          {Object.entries(dataCategoryLabels).map(([key, label]) => (
            <div key={key}>
              <CheckBox
                checked={formData.data_categories.includes(key as DataCategory)}
                onChange={() => toggleArrayValue('data_categories', key as DataCategory)}
                text={label}
              />
              {key === DataCategory.HEALTH && formData.data_categories.includes(key as DataCategory) && (
                <MessageStrip design="Warning" hideCloseButton style={{ marginLeft: '1.75rem', marginTop: '0.25rem' }}>
                  Besondere Kategorie nach Art. 9 DSGVO - erhöhte Anforderungen!
                </MessageStrip>
              )}
            </div>
          ))}
        </FlexBox>
      </div>
    </div>
  );

  // Step 4: Empfänger
  const renderStep4 = () => (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <Label>Empfänger der Daten</Label>
          <HelpTooltip text={fieldHelp.processing.recipients} inline />
        </div>
        <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.5rem' }}>
          An wen werden die Daten weitergegeben? (z.B. Dienstleister, Behörden)
        </Text>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Input
            value={recipientInput}
            onInput={(e: any) => setRecipientInput(e.target.value)}
            placeholder="Empfänger hinzufügen"
            style={{ flex: 1 }}
            onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
          />
          <Button icon="add" onClick={addRecipient}>Hinzufügen</Button>
        </FlexBox>

        {formData.recipients.length > 0 && (
          <FlexBox direction="Column" style={{ gap: '0.25rem', marginTop: '0.75rem' }}>
            {formData.recipients.map((recipient, index) => (
              <FlexBox
                key={index}
                alignItems="Center"
                justifyContent="SpaceBetween"
                style={{
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'var(--sapList_Background, #fff)',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--sapList_BorderColor, #e5e5e5)'
                }}
              >
                <Text>{recipient}</Text>
                <Button icon="delete" design="Transparent" onClick={() => removeRecipient(index)} tooltip="Entfernen" />
              </FlexBox>
            ))}
          </FlexBox>
        )}
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <CheckBox
            checked={formData.third_country_transfer}
            onChange={(e: any) => updateFormData('third_country_transfer', e.target.checked)}
            text="Datenübermittlung in Drittländer (außerhalb EU/EWR)"
          />
          <HelpTooltip text={fieldHelp.processing.third_country_transfer} inline />
        </span>
      </div>

      {formData.third_country_transfer && (
        <div>
          <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Details zur Drittlandübermittlung</Label>
          <TextArea
            value={formData.third_country_details}
            onInput={(e: any) => updateFormData('third_country_details', e.target.value)}
            placeholder="Welche Länder? Welche Garantien? (z.B. Standardvertragsklauseln)"
            rows={4}
            style={{ width: '100%' }}
          />
        </div>
      )}
    </div>
  );

  // Step 5: Löschung & Risiko
  const renderStep5 = () => (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <Label required>Löschfrist (in Monaten)</Label>
          <HelpTooltip text={fieldHelp.processing.retention_period} inline />
        </div>
        <Input
          type="Number"
          value={formData.retention_period.toString()}
          onInput={(e: any) => updateFormData('retention_period', parseInt(e.target.value) || 0)}
          style={{ width: '100%' }}
        />
        <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.25rem' }}>
          Nach wie vielen Monaten werden die Daten gelöscht?
        </Text>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Details zur Löschung</Label>
        <TextArea
          value={formData.retention_details}
          onInput={(e: any) => updateFormData('retention_details', e.target.value)}
          placeholder="z.B. gesetzliche Aufbewahrungsfristen, Löschkonzept"
          rows={3}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>TOM-Referenz</Label>
        <Input
          value={formData.tom_reference || ''}
          onInput={(e: any) => updateFormData('tom_reference', e.target.value)}
          placeholder="Verweis auf technische und organisatorische Maßnahmen"
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <Label>Risikoeinschätzung</Label>
          <HelpTooltip text={fieldHelp.processing.risk_level} inline />
        </div>
        <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
          {Object.entries(riskLevelConfig).map(([key, config]) => {
            const isSelected = formData.risk_level === key;
            return (
              <div
                key={key}
                onClick={() => updateFormData('risk_level', key as RiskLevel)}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: isSelected
                    ? '2px solid var(--sapBrandColor)'
                    : '1px solid var(--sapField_BorderColor)',
                  backgroundColor: isSelected ? 'rgba(0, 112, 242, 0.06)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
                  <RadioButton
                    name="risk_level"
                    checked={isSelected}
                    onChange={() => updateFormData('risk_level', key as RiskLevel)}
                    text={config.label}
                  />
                  <Icon
                    name={config.icon}
                    style={{
                      color: config.state === 'Success' ? 'var(--sapPositiveColor)' :
                             config.state === 'Warning' ? 'var(--sapCriticalColor)' :
                             'var(--sapNegativeColor)'
                    }}
                  />
                </FlexBox>
                <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginLeft: '1.75rem', display: 'block' }}>
                  {config.description}
                </Text>
              </div>
            );
          })}
        </FlexBox>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderStep1();
      case 1: return renderStep2();
      case 2: return renderStep3();
      case 3: return renderStep4();
      case 4: return renderStep5();
      default: return null;
    }
  };

  return (
    <div className="page-layout--narrow">
      <Button
        icon="nav-back"
        design="Transparent"
        onClick={() => navigate('/processing')}
        style={{ marginBottom: '0.75rem' }}
      >
        Zurück
      </Button>

      <Title level="H3" style={{ marginBottom: '1rem' }}>
        {editMode ? `Verarbeitung bearbeiten: ${formData.name}` : 'Neue Verarbeitung erstellen'}
      </Title>

      {/* Step Indicator */}
      <div style={{ marginBottom: '1.5rem' }}>
        <ProgressIndicator value={(currentStep + 1) * 20} displayValue={`Schritt ${currentStep + 1} von 5`} />
        <FlexBox justifyContent="SpaceBetween" style={{ marginTop: '0.5rem' }}>
          {steps.map((step, i) => (
            <FlexBox key={i} alignItems="Center" style={{ gap: '0.25rem' }}>
              <Icon name={step.icon} style={{
                fontSize: '0.875rem',
                color: i === currentStep ? 'var(--sapBrandColor)' :
                       i < currentStep ? 'var(--sapPositiveColor)' : 'var(--sapNeutralColor)'
              }} />
              <Text style={{
                fontSize: '0.75rem',
                fontWeight: i === currentStep ? '600' : '400',
                color: i === currentStep ? 'var(--sapBrandColor)' :
                       i < currentStep ? 'var(--sapPositiveColor)' : 'var(--sapNeutralTextColor)'
              }}>
                {step.title}
              </Text>
            </FlexBox>
          ))}
        </FlexBox>
      </div>

      {/* Error */}
      {error && (
        <MessageStrip design="Negative" hideCloseButton style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {/* Step Content */}
      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1.5rem' }}>
          {renderStepContent()}
        </div>
      </Card>

      {/* Navigation */}
      <FlexBox justifyContent="SpaceBetween">
        <Button onClick={() => navigate('/processing')} design="Transparent">Abbrechen</Button>
        <FlexBox style={{ gap: '0.5rem' }}>
          {currentStep > 0 && (
            <Button onClick={handleBack} icon="nav-back">Zurück</Button>
          )}
          {currentStep < 4 ? (
            <Button onClick={handleNext} design="Emphasized" icon="feeder-arrow">
              Weiter
            </Button>
          ) : (
            <Button onClick={handleSubmit} design="Emphasized" icon="save" disabled={loading}>
              {loading ? 'Wird gespeichert...' : (editMode ? 'Änderungen speichern' : 'Speichern')}
            </Button>
          )}
        </FlexBox>
      </FlexBox>
    </div>
  );
}
